import React, { useEffect, useRef, useState } from 'react';
import { useChatWidgetContext } from '../../ChatWidgetContext';
import { ChatMessage } from '../../types';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import styles from '../ChatWidget.module.scss';

function useTypewriterText(fullText: string, charsPerTick = 2, intervalMs = 16): string {
  const [displayed, setDisplayed] = useState('');
  const indexRef = useRef(0);

  useEffect(() => {
    if (!fullText) {
      setDisplayed('');
      indexRef.current = 0;
      return;
    }

    if (indexRef.current >= fullText.length) {
      setDisplayed(fullText);
      return;
    }

    const timer = setInterval(() => {
      indexRef.current = Math.min(indexRef.current + charsPerTick, fullText.length);
      setDisplayed(fullText.slice(0, indexRef.current));

      if (indexRef.current >= fullText.length) {
        clearInterval(timer);
      }
    }, intervalMs);

    return () => clearInterval(timer);
  }, [fullText, charsPerTick, intervalMs]);

  return displayed;
}

function StreamingBubble(props: { text: string; isGrouped: boolean }) {
  const displayedText = useTypewriterText(props.text);

  const streamingMessage: ChatMessage = {
    role: 'assistant',
    content: displayedText,
    timestamp: new Date(),
  };

  return <MessageBubble message={streamingMessage} isGrouped={props.isGrouped} />;
}

function isAssistantSide(role: string): boolean {
  return role === 'assistant' || role === 'tool-activity';
}

export default function MessageList() {
  const {
    messages,
    streamingText,
    isStreaming,
    isToolExecuting,
  } = useChatWidgetContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, streamingText]);

  const showTypingIndicator = isStreaming && !streamingText && !isToolExecuting;
  const lastMessage = messages[messages.length - 1];
  const streamingIsGrouped = !!lastMessage && isAssistantSide(lastMessage.role);

  const lastToolActivityIndex = messages.reduce<number>(
    (acc, msg, i) => (msg.role === 'tool-activity' ? i : acc),
    -1,
  );

  return (
    <div className={styles.messageList} ref={scrollRef} data-testid="chat-message-list">
      <div className={styles.dateSeparator}>Today</div>
      {messages.map((message, index) => {
        const prev = index > 0 ? messages[index - 1] : undefined;
        const grouped = !!prev
          && isAssistantSide(message.role)
          && isAssistantSide(prev.role);
        const isActiveToolCall = isToolExecuting
          && message.role === 'tool-activity'
          && index === lastToolActivityIndex;
        return (
          <MessageBubble
            key={index}
            message={message}
            isGrouped={grouped}
            isActiveToolCall={isActiveToolCall}
          />
        );
      })}
      {showTypingIndicator && <TypingIndicator />}
      {streamingText && <StreamingBubble text={streamingText} isGrouped={streamingIsGrouped} />}
    </div>
  );
}
